type ToolInput = Record<string, unknown> | null | undefined

const TOOL_LABELS: Record<string, string> = {
  Read: 'Reading file',
  Write: 'Writing file',
  Edit: 'Editing file',
  Glob: 'Finding files',
  Grep: 'Searching files',
  TodoWrite: 'Planning',
  apply_patch: 'Editing files',
  api_list_data_sources: 'Checking data sources',
  api_describe_data_source: 'Inspecting data source',
  api_list_observables: 'Listing metrics',
  api_read_observable: 'Reading metric',
  db_admin_read: 'Querying data',
  db_admin_sample: 'Sampling data',
  db_admin_coverage: 'Checking data coverage',
  api_compute_metrics: 'Computing metrics',
  api_find_similar_days: 'Finding similar days',
  api_run_script_backtest: 'Running backtest',
  api_create_thesis: 'Creating thesis',
  api_set_thesis_decomposition: 'Decomposing thesis',
  api_list_theses: 'Listing theses',
  api_get_thesis: 'Reading thesis',
  api_read_thesis_trail: 'Reading thesis trail',
  api_list_runs: 'Listing runs',
  api_get_run_spans: 'Reading run detail',
  pnl_thesis_performance: 'Reading performance',
  pnl_backtest_vs_live: 'Comparing backtest vs live',
  pipeline_lifecycle: 'Inspecting pipeline',
  pipeline_run_tree: 'Inspecting pipeline',
  pipeline_script: 'Reading script',
  pipeline_script_runs: 'Reading script runs',
  api_list_crons: 'Listing schedules',
  api_create_cron: 'Creating schedule',
  api_update_cron: 'Updating schedule',
  api_delete_cron: 'Deleting schedule',
  api_run_cron_now: 'Running schedule now',
}

// High-signal domain keywords → what the agent is actually fetching/doing.
// Matched against the whole (unwrapped) command so heredoc scripts count too.
const DOMAIN_RULES: Array<[RegExp, string]> = [
  [/\brdn\b|tge_rdn|fixing\d|day[-_ ]?ahead/, 'Fetching RDN day-ahead prices'],
  [/\brdb\b|rdbee|intraday|continuous/, 'Fetching intraday prices'],
  [/entso/, 'Fetching ENTSO-E data'],
  [/\btge\b/, 'Fetching TGE market data'],
  [/pse_|wind_mw|pv_mw|demand_mw|dispatch|imbalance|saldo|generation/, 'Fetching generation data'],
  [/temperature|weather|era5|open[-_ ]?meteo|\bdwd\b|ecmwf/, 'Fetching weather data'],
  [/gas[-_ ]?storage|agsi/, 'Fetching gas-storage data'],
  [/\bfx\b|exchange[-_ ]?rate|\bnbp\b/, 'Fetching FX data'],
  [/pnl|backtest|performance/, 'Analyzing performance'],
  [/list_data_sources|describe_data_source|data[-_ ]?source/, 'Checking data sources'],
  [/\bcron\b|schedule/, 'Managing schedules'],
  [/thesis|theses/, 'Working with theses'],
  [/db_admin|duckdb|\bselect .*\bfrom\b|\.sql\b/, 'Querying data'],
]

// Verb-based fallback from the command's structure.
const VERB_RULES: Array<[RegExp, string]> = [
  [/cat\s*>|(^|\s)tee\s|<<-?\s*['"]?\w/, 'Writing script'],
  [/(^|\s)(python3?|uv run|pytest)\b|\.py\b/, 'Running analysis'],
  [/(^|\s)(ls|find|fd)\b/, 'Listing files'],
  [/(^|\s)(rg|grep|ag)\b/, 'Searching files'],
  [/(^|\s)(cat|head|tail|less|bat)\b/, 'Reading files'],
  [/(^|\s)(sed|awk)\b/, 'Editing text'],
  [/pip install|uv pip|npm (i|install|ci)\b/, 'Installing packages'],
  [/(^|\s)(curl|wget)\b/, 'Fetching data'],
  [/(^|\s)git\b/, 'Running git'],
]

function commandString(input: ToolInput): string {
  if (!input) return ''
  const c = input.command
  if (typeof c === 'string') return c
  if (Array.isArray(c)) return c.map((x) => String(x)).join(' ')
  return ''
}

function unwrapShell(raw: string): string {
  const cmd = raw.trim()
  const quoted = cmd.match(/^(?:\S*\/)?(?:zsh|bash|sh)\s+-l?c\s+(['"])([\s\S]*)\1\s*$/)
  if (quoted) return quoted[2].trim()
  const bare = cmd.match(/^(?:\S*\/)?(?:zsh|bash|sh)\s+-l?c\s+([\s\S]+)$/)
  if (bare) return bare[1].trim()
  return cmd
}

function describeCommand(raw: string): string {
  if (!raw.trim()) return 'Running command'
  const cmd = unwrapShell(raw)
  if (cmd === 'true' || cmd === ':' || cmd === '') return 'Running command'
  const lc = cmd.toLowerCase()
  for (const [re, label] of DOMAIN_RULES) if (re.test(lc)) return label
  for (const [re, label] of VERB_RULES) if (re.test(lc)) return label
  const first = cmd.split(/\s*(?:&&|\|\||[|;])\s*/)[0].replace(/\s+/g, ' ').trim()
  return first.length > 48 ? `${first.slice(0, 48)}…` : first || 'Running command'
}

function prettify(base: string): string {
  const spaced = base.replace(/[_-]+/g, ' ').trim()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export function toolLabel(name: string, input?: ToolInput): string {
  const base = name.startsWith('mcp__') ? (name.split('__').pop() ?? name) : name
  if (base === 'shell' || base === 'local_shell' || base === 'Bash' || base === 'bash') {
    return describeCommand(commandString(input))
  }
  if (base === 'WebSearch') {
    const q = typeof input?.query === 'string' ? input.query : ''
    return q ? `Searching the web: ${q.length > 40 ? `${q.slice(0, 40)}…` : q}` : 'Searching the web'
  }
  const known = TOOL_LABELS[base]
  if (known) {
    const src = typeof input?.source === 'string' ? input.source : ''
    return src ? `${known}: ${src}` : known
  }
  return prettify(base)
}
